import axios from 'axios';

// 获取 API 基础 URL - 兼容 Vite 和 React 环境变量
const getApiBaseUrl = () => {
  // Vite 环境变量（优先）
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // React 环境变量
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // 生产环境使用相对路径
  if (import.meta.env.PROD || process.env.NODE_ENV === 'production') {
    return '';
  }
  // 开发环境默认
  return 'http://localhost:18080';
};

// 获取完整的 API URL（带 /api 路径）
export const getFullApiUrl = () => {
  const base = getApiBaseUrl();
  return base ? `${base}/api` : '/api';
};

const API_BASE_URL = getFullApiUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('user_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response) {
      // 兼容多种错误格式: {message}, {error: {message}}, {error: "str"}
      const data = error.response?.data || {};
      const serverMsg = data.message
        || data.error?.message
        || (typeof data.error === 'string' ? data.error : '')
        || '';
      switch (error.response.status) {
        case 401:
          localStorage.removeItem('user_token');
          window.location.href = '/login';
          break;
        case 403:
          console.error('权限不足:', serverMsg || error.response.data);
          break;
        default:
          console.error('请求失败:', error.response.data);
      }
      // 将服务端错误消息挂到 error 对象上，方便调用方提取
      if (serverMsg) {
        error.serverMessage = serverMsg;
      }
    }
    return Promise.reject(error);
  }
);

// 用户相关 API
export const userAPI = {
  // 注册
  register: (email, password, referrerCode = '', verifyCode = '', campaignCode = '') => {
    const data = { email, password, referrer_code: referrerCode };
    if (verifyCode) {
      data.verify_code = verifyCode;
    }
    if (campaignCode) {
      data.campaign_code = campaignCode;
    }
    return api.post('/register', data);
  },

  // 登录
  login: (email, password) => api.post('/login', { email, password }),

  // 登出
  logout: () => {
    localStorage.removeItem('user_token');
    window.location.href = '/login';
  },

  // 获取用户信息
  getInfo: () => api.get('/user'),

  // 用户端功能开关（如 w3_studio_enabled，管理后台控制）
  getFeatures: () => api.get('/features'),

  // 查询余额
  getQuota: () => api.get('/quota'),

  // 充值 - 创建支付会话（支持Stripe、PayPal和FastSpring）
  // rechargeType: 'online' 在线充值, 'offline' 线下充值
  recharge: (amount, currency = 'usd', method = 'stripe', rechargeType = 'online') => api.post('/recharge', { amount, currency, method, recharge_type: rechargeType }),

  // 获取可用的支付方式列表
  getPaymentMethods: () => api.get('/payment-methods'),

  // 创建 FastSpring 结账会话
  createFastSpringSession: (productId) => api.post('/fastspring/session', { product_id: productId }),
};

// API Key 相关
export const apiKeyAPI = {
  // 获取当前用户的 Key 列表
  list: () => api.get('/api-keys'),

  // 创建新的 Key
  create: (name, creditLimit = 0) => api.post('/api-keys', { name, credit_limit: creditLimit }),

  // 更新 Key 信息
  update: (id, data) => api.put(`/api-keys/${id}`, data),

  // 删除 Key
  delete: (id) => api.delete(`/api-keys/${id}`),

  // 启用/禁用 Key
  toggle: (id) => api.post(`/api-keys/${id}/toggle`),

  // 获取 Key 的用量记录
  getUsage: (id, limit = 50) => api.get(`/api-keys/${id}/usage`, { params: { limit } }),

  // 重置 API Key（旧方法，保留兼容）
  reset: () => api.post('/user/reset-key'),
};

// 账单相关
export const billsAPI = {
  // 获取账单列表
  getList: (page = 1, pageSize = 20) => api.get('/bills', { params: { page, page_size: pageSize } }),
};

// 充值订单相关
export const ordersAPI = {
  // 获取充值记录列表
  getList: (page = 1, pageSize = 20) => api.get('/orders', { params: { page, page_size: pageSize } }),
  // 获取余额调整记录
  getBalanceAdjustments: (page = 1, pageSize = 20) => api.get('/balance-adjustments', { params: { page, page_size: pageSize } }),
};

// 代理商相关
export const affiliateAPI = {
  // 获取代理商信息
  getInfo: () => api.get('/affiliate'),
  // 获取代理商统计
  getStats: () => api.get('/affiliate/stats'),
  // 申请提现
  withdraw: (data) => api.post('/affiliate/withdraw', data),
};

// 模型相关
export const modelAPI = {
  // 获取可用模型列表
  getAvailable: () => api.get('/models'),
  // 获取模型广场列表
  getSquareModels: (params) => api.get('/models/square', { params }),
  // 获取热门模型列表
  getHotModels: () => api.get('/models/square', { params: { hot: 'true' } }),
  // 获取模型类型
  getModelTypes: () => api.get('/models/types'),
  // 获取应用场景
  getUseCases: () => api.get('/models/usecases'),
};

// 邀请返利相关
export const referralAPI = {
  // 获取当前有效活动
  getActiveCampaign: () => api.get('/referral/campaign'),
  // 获取我的返利记录
  getRecords: () => api.get('/referral/records'),
};

// 价格比较相关
export const pricingAPI = {
  // 获取所有模型价格
  getPricing: () => api.get('/v1/pricing'),
  // 获取价格历史
  getPriceHistory: (modelName, days = 30) => api.get('/v1/pricing/history', { params: { model: modelName, days } }),
  // 获取价格告警列表
  getPriceAlerts: () => api.get('/v1/pricing/alerts/mine'),
  // 创建价格告警
  createPriceAlert: (data) => api.post('/v1/pricing/alerts', data),
  // 删除价格告警
  deletePriceAlert: (id) => api.delete(`/v1/pricing/alerts/${id}`),
};

// 批量处理相关
export const batchAPI = {
  // 创建批量任务
  create: (data) => api.post('/v1/batches', data),
  // 列出批量任务
  list: () => api.get('/v1/batches'),
  // 获取批量任务详情
  get: (id) => api.get(`/v1/batches/${id}`),
  // 取消批量任务
  cancel: (id) => api.post(`/v1/batches/${id}/cancel`),
  // 删除批量任务
  delete: (id) => api.delete(`/v1/batches/${id}`),
  // 获取批量任务结果
  getResult: (id) => api.get(`/v1/batches/${id}/result`),
  // 上传文件
  uploadFile: (formData) => api.post('/v1/files', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// 问题反馈相关
export const feedbackAPI = {
  // 提交反馈
  submit: (data) => api.post('/feedback', data),
  // 获取我的反馈列表
  getList: () => api.get('/feedback'),
  // 用户追加回复
  addReply: (id, content) => api.post(`/feedback/${id}/reply`, { content }),
  // 获取反馈消息列表
  getMessages: (id) => api.get(`/feedback/${id}/messages`),
};

// 系统配置相关（公开接口）
export const configAPI = {
  // 获取所有系统配置
  getConfigs: () => api.get('/configs'),
  // 获取首页配置（底部链接、背景图等）
  getHomepageConfig: () => api.get('/homepage-config'),
};

// AI 调用 API
export const chatAPI = {
  // 聊天完成（同步）
  // 使用 hk-api (18080)，它使用 JWT 认证并支持 key_id 参数
  completions: (data) => api.post('/api/v1/chat/completions', data, {
    baseURL: getFullApiUrl(),
  }),

  // 聊天完成（流式）- 返回 { abort, promise } 以支持取消
  // 使用 hk-api (18080)，它使用 JWT 认证并支持 key_id 参数
  completionsStream: (data, onChunk, onError, onComplete) => {
    // 用户端调用 hk-api (18080)，它使用 JWT 认证并支持 key_id 参数
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const abortController = new AbortController();
    
    // 超时控制器（5分钟超时）
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, 300000); // 5分钟

    console.log('[Stream] Starting request to:', `${baseURL}/v1/chat/completions`);
    console.log('[Stream] Request data:', data);

    const promise = fetch(`${baseURL}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      signal: abortController.signal,
    })
      .then(async (response) => {
        console.log('[Stream] Response status:', response.status);
        console.log('[Stream] Content-Type:', response.headers.get('content-type'));
        
        if (!response.ok) {
          const errorText = await response.text();
          let errorMsg = extractErrorFromResponse(errorText, response.status);
          if (!errorMsg) {
            errorMsg = `HTTP error! status: ${response.status}`;
          }
          throw new Error(errorMsg);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let chunkCount = 0;

        while (true) {
          const { done, value } = await reader.read();
          
          // 先处理当前块的数据（如果有）
          if (value) {
            const decoded = decoder.decode(value, { stream: true });
            console.log('[Stream] Received chunk:', decoded.substring(0, 100));
            buffer += decoded;
            chunkCount++;
          }
          
          // SSE 用双换行符分隔事件
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            const line = event.trim();
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                continue;
              }
              try {
                const parsed = JSON.parse(data);
                onChunk?.(parsed);
              } catch (e) {
                console.error('Failed to parse SSE data:', data);
              }
            }
          }
          
          // 检查流是否结束
          if (done) {
            // 处理 buffer 中剩余的数据
            if (buffer.trim()) {
              const line = buffer.trim();
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data !== '[DONE]') {
                  try {
                    const parsed = JSON.parse(data);
                    onChunk?.(parsed);
                  } catch (e) {
                    console.error('Failed to parse remaining SSE data:', data);
                  }
                }
              }
            }
            clearTimeout(timeoutId);
            console.log('[Stream] Stream complete, total chunks:', chunkCount);
            onComplete?.();
            break;
          }
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          // 检查是否是超时
          if (err.message === 'canceled' && !abortController.signal.aborted) {
            console.log('Request timeout');
            onError?.(new Error('请求超时，请重试'));
          } else {
            console.log('Request aborted');
          }
        } else {
          onError?.(err);
        }
      });

    return {
      abort: () => abortController.abort(),
      promise,
    };
  },
};

// 多模态模型 API
export const multimodalAPI = {
  // 图片生成
  generateImage: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/images/generations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }).then(async res => {
      const text = await res.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      try {
        const json = JSON.parse(text);
        if (!res.ok) {
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        }
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) {
          // JSON 解析失败 → 可能是 HTML 错误页面
          const errorMsg = extractErrorFromResponse(text, res.status);
          throw new Error(errorMsg);
        }
        // 已是格式化错误，直接抛出
        throw e;
      }
    });
  },

  // 语音合成 - 返回原始二进制响应
  textToSpeech: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/speech`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }).then(async res => {
      if (!res.ok) {
        const text = await res.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        } catch (e) {
          if (e instanceof SyntaxError) {
            const errorMsg = extractErrorFromResponse(text, res.status);
            throw new Error(errorMsg || `HTTP ${res.status}`);
          }
          throw e;
        }
      }
      // 返回二进制音频数据
      const blob = await res.blob();
      return { blob, contentType: res.headers.get('Content-Type') };
    });
  },

  // 视频生成 - 返回原始响应
  generateVideo: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/videos/generations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }).then(async res => {
      const text = await res.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      try {
        const json = JSON.parse(text);
        if (!res.ok) {
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        }
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) {
          const errorMsg = extractErrorFromResponse(text, res.status);
          throw new Error(errorMsg);
        }
        throw e;
      }
    });
  },

  // 查询视频状态
  getVideoStatus: (taskId, model) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/videos/generations/${taskId}?model=${model}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then(async res => {
      const text = await res.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      try {
        const json = JSON.parse(text);
        if (!res.ok) {
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        }
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) {
          const errorMsg = extractErrorFromResponse(text, res.status);
          throw new Error(errorMsg);
        }
        throw e;
      }
    });
  },

  // 获取用户图片素材历史（Seedream 生成记录，24h 有效）
  getImageHistory: (page = 1, pageSize = 20) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/images/history?page=${page}&page_size=${pageSize}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then(async res => {
      const text = await res.text();
      if (!text) throw new Error('Empty response');
      try {
        const json = JSON.parse(text);
        if (!res.ok) throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) throw new Error(`HTTP ${res.status}: ${text.substring(0, 200)}`);
        throw e;
      }
    });
  },

  // 获取单条图片记录URL（自动刷新过期签名URL）
  getImageRecordURL: (recordId) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/images/record/${recordId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 删除单条图片历史记录
  deleteImageHistory: (recordId) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/images/history/${recordId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then(async res => {
      const text = await res.text();
      if (!text) throw new Error('Empty response');
      try {
        const json = JSON.parse(text);
        if (!res.ok) throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) throw new Error(`HTTP ${res.status}: ${text.substring(0, 200)}`);
        throw e;
      }
    });
  },

  // 获取用户视频素材历史（Seedance/Wanx 生成记录，24h 有效）
  getVideoHistory: (page = 1, pageSize = 20) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/videos/history?page=${page}&page_size=${pageSize}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then(async res => {
      const text = await res.text();
      if (!text) throw new Error('Empty response');
      try {
        const json = JSON.parse(text);
        if (!res.ok) throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) throw new Error(`HTTP ${res.status}: ${text.substring(0, 200)}`);
        throw e;
      }
    });
  },

  // 删除单条视频历史记录
  deleteVideoHistory: (recordId) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/videos/history/${recordId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then(async res => {
      const text = await res.text();
      if (!text) throw new Error('Empty response');
      try {
        const json = JSON.parse(text);
        if (!res.ok) throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) throw new Error(`HTTP ${res.status}: ${text.substring(0, 200)}`);
        throw e;
      }
    });
  },

  // Embedding
  createEmbedding: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/embeddings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }).then(async res => {
      const text = await res.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      try {
        const json = JSON.parse(text);
        if (!res.ok) {
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        }
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) {
          const errorMsg = extractErrorFromResponse(text, res.status);
          throw new Error(errorMsg);
        }
        throw e;
      }
    });
  },

  // Rerank（重排序）
  createRerank: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/rerank`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }).then(async res => {
      const text = await res.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      try {
        const json = JSON.parse(text);
        if (!res.ok) {
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        }
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) {
          const errorMsg = extractErrorFromResponse(text, res.status);
          throw new Error(errorMsg);
        }
        throw e;
      }
    });
  },

  // Transcription（语音转写）
  createTranscription: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/transcriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }).then(async res => {
      const text = await res.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      try {
        const json = JSON.parse(text);
        if (!res.ok) {
          throw new Error(json.message || json.error?.message || `HTTP ${res.status}`);
        }
        return json;
      } catch (e) {
        if (e instanceof SyntaxError) {
          const errorMsg = extractErrorFromResponse(text, res.status);
          throw new Error(errorMsg);
        }
        throw e;
      }
    });
  },

  // Qwen TTS 语音合成（返回 JSON 含音频 URL）
  generateSpeech: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/speech`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data),
    }).then(res => res.json());
  },

  // 查询 ASR 任务状态（通过网关代理，避免 CORS）
  getTaskStatus: (taskId) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/transcriptions/${taskId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 上传音频文件到 OSS（通过网关代理）
  uploadAudio: (file, model = 'qwen3-asr-flash-filetrans') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('model', model);
    return fetch(`${baseURL}/v1/audio/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    }).then(res => res.json());
  },

  // 声音复刻：上传音频样本 → 创建专属音色
  voiceClone: (audioUrl, preferredName = 'my_voice') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/voice-clone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ audio_url: audioUrl, preferred_name: preferredName }),
    }).then(res => res.json());
  },

  // 声音设计：文字描述 → 创建新音色
  voiceDesign: (voicePrompt, previewText, preferredName = 'custom_voice') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/voice-design`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ voice_prompt: voicePrompt, preview_text: previewText, preferred_name: preferredName }),
    }).then(res => res.json());
  },

  // 获取语音历史记录
  getAudioHistory: (page = 1, pageSize = 30) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/history?page=${page}&page_size=${pageSize}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 删除语音历史记录
  deleteAudioHistory: (id) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/history/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 获取用户自定义音色列表
  getUserVoices: () => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/voices`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 删除自定义音色
  deleteUserVoice: (voiceId) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/voices/${encodeURIComponent(voiceId)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 清空所有语音历史记录
  clearAudioHistory: () => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/v1/audio/history`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },

  // 聊天（通用，支持多模态）
  chat: (data, options = {}) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    
    // 直接返回 fetch Response 对象，用于流式读取
    // 确保返回的是原生 Response 对象，包含 getReader 方法
    return fetch(`${baseURL}/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      signal: options.signal,
    });
  },

  // ========== XRToken 素材库 API ==========
  createAssetGroup: (name, description = '', externalUserId = '', type = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const body = { name, description };
    if (externalUserId) body.external_user_id = String(externalUserId);
    if (type) body.type = type;
    return fetch(`${baseURL}/asset-groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    }).then(res => res.json());
  },
  listAssetGroups: (type = '', externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (externalUserId) params.set('external_user_id', String(externalUserId));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetch(`${baseURL}/asset-groups${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },
  updateAssetGroup: (groupId, name, externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const body = {};
    if (name !== undefined) body.name = name;
    if (externalUserId) body.external_user_id = String(externalUserId);
    return fetch(`${baseURL}/asset-groups/${groupId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    }).then(res => res.text().then(t => { try { return JSON.parse(t); } catch(e) { return {}; } }));
  },
  createAsset: (group_id, url, asset_type = 'Image', name = '', externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const body = { group_id, url, asset_type, name };
    if (externalUserId) body.external_user_id = String(externalUserId);
    return fetch(`${baseURL}/assets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    }).then(res => res.json());
  },
  listAssets: (group_id = '', externalUserId = '', type = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const params = new URLSearchParams();
    if (group_id) params.set('group_id', group_id);
    if (externalUserId) params.set('external_user_id', String(externalUserId));
    if (type) params.set('type', type);
    // 不传 statuses 参数: 返回全部状态 (Active + Failed)
    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetch(`${baseURL}/assets${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },
  deleteAsset: (assetId, externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const params = new URLSearchParams();
    if (externalUserId) params.set('external_user_id', String(externalUserId));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetch(`${baseURL}/assets/${assetId}${qs}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.text().then(t => { try { return JSON.parse(t); } catch(e) { return {}; } }));
  },
  deleteAssetGroup: (groupId, externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const params = new URLSearchParams();
    if (externalUserId) params.set('external_user_id', String(externalUserId));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetch(`${baseURL}/asset-groups/${groupId}${qs}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.text().then(t => { try { return JSON.parse(t); } catch(e) { return {}; } }));
  },
  uploadFile: (file) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', 'asset');
    return fetch(`${baseURL}/files`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    }).then(res => res.json());
  },
  getFaceConsent: (externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    let url = `${baseURL}/face-consent`;
    if (externalUserId) url += `?external_user_id=${encodeURIComponent(externalUserId)}`;
    return fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(res => res.json());
  },
  upsertFaceConsent: (data) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/face-consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data),
    }).then(res => res.json());
  },
  createValidateSession: (body = {}) => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    return fetch(`${baseURL}/asset-groups/validate-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    }).then(res => res.json());
  },
  getValidateResult: (bytedToken, externalUserId = '') => {
    const baseURL = getFullApiUrl();
    const token = localStorage.getItem('user_token');
    const body = { bytedToken };
    if (externalUserId) body.external_user_id = String(externalUserId);
    return fetch(`${baseURL}/asset-groups/validate-result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    }).then(res => res.json());
  },
};

// 从响应中提取错误信息（处理 xrtoken JSON 错误 + HTML 错误页面）
function extractErrorFromResponse(text, statusCode) {
  // 检查是否是 HTML 响应
  if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
    // 尝试从 HTML 中提取错误信息
    const titleMatch = text.match(/<title[^>]*>([^<]+)<\/title>/i);
    if (titleMatch) {
      return `Server error (${statusCode}): ${titleMatch[1]}`;
    }
    
    // 尝试提取 h1 或其他错误提示
    const h1Match = text.match(/<h1[^>]*>([^<]+)<\/h1>/i);
    if (h1Match) {
      return `Server error (${statusCode}): ${h1Match[1]}`;
    }
    
    // 尝试提取 error 或 message 类名下的内容
    const errorMatch = text.match(/class=\"[^\"]*error[^\"]*\"[^>]*>([^<]+)/i);
    if (errorMatch) {
      return `Server error (${statusCode}): ${errorMatch[1].trim()}`;
    }
    
    return `Server returned HTML error page (${statusCode}). Please check the model configuration.`;
  }
  
  // 尝试解析 xrtoken JSON 错误格式: {"error": {"code": "...", "message": "..."}}
  // 以及常见格式: {"message": "..."}, {"error": "..."}
  try {
    const json = JSON.parse(text);
    if (json.error?.message) {
      return json.error.message;
    }
    if (typeof json.error === 'string') {
      return json.error;
    }
    if (json.message) {
      return json.message;
    }
  } catch {
    // 不是有效 JSON，继续使用原始文本
  }
  
  // 如果不是 HTML，返回原始文本的前 500 字符
  return text.substring(0, 500);
}

// Preset API (用户预设管理)
export const presetAPI = {
  // 获取公开预设列表
  getPublicPresets: (params) => api.get('/v1/presets/public', { params }),

  // 获取我的预设
  getMyPresets: (params) => api.get('/v1/presets', { params }),

  // 获取单个预设
  getPreset: (id) => api.get(`/v1/presets/${id}`),

  // 创建预设
  createPreset: (data) => api.post('/v1/presets', data),

  // 更新预设
  updatePreset: (id, data) => api.put(`/v1/presets/${id}`, data),

  // 删除预设
  deletePreset: (id) => api.delete(`/v1/presets/${id}`),

  // 复制预设
  copyPreset: (id) => api.post(`/v1/presets/${id}/copy`),

  // 点赞/取消点赞
  toggleLike: (id) => api.post(`/v1/presets/${id}/like`),

  // 获取点赞状态
  getLikeStatus: (id) => api.get(`/v1/presets/${id}/like`),

  // 获取我点赞的预设列表
  getMyLikedPresets: (params) => api.get('/v1/presets/liked', { params }),

  // 增加使用次数
  incrementUsage: (id) => api.post(`/v1/presets/${id}/increment-usage`),
};

// UserModelConfig API (用户模型配置管理 - 训练好的模型参数)
export const userModelConfigAPI = {
  // 获取公开的模型配置列表
  getPublicConfigs: (params) => api.get('/v1/user-model-configs/public', { params }),

  // 获取我的模型配置
  getMyConfigs: (params) => api.get('/v1/user-model-configs', { params }),

  // 获取单个模型配置
  getConfig: (id) => api.get(`/v1/user-model-configs/${id}`),

  // 创建模型配置
  createConfig: (data) => api.post('/v1/user-model-configs', data),

  // 更新模型配置
  updateConfig: (id, data) => api.put(`/v1/user-model-configs/${id}`, data),

  // 删除模型配置
  deleteConfig: (id) => api.delete(`/v1/user-model-configs/${id}`),

  // 复制模型配置
  copyConfig: (id) => api.post(`/v1/user-model-configs/${id}/copy`),

  // 获取用户在特定模型上的配置
  getConfigForModel: (modelName) => api.get(`/v1/user-model-configs/model/${modelName}`),

  // 增加使用次数
  incrementUsage: (id) => api.post(`/v1/user-model-configs/${id}/increment-usage`),
};

export default api;
